'use client';

import type { VariantOption } from '@/lib/validators/variant-options';
import { Switch } from './switch';
import styles from './direction-cards.module.css';

interface DirectionCardsProps {
  /** The proposed directions, in proposal order. */
  options: VariantOption[];
  /** Ids the user switched ON. Controlled — the caller owns the decisions. */
  chosen: string[];
  onToggle: (optionId: string, on: boolean) => void;
  /** Accessible name for one card's switch (interpolates the card title). */
  toggleLabel: (option: VariantOption) => string;
}

/**
 * DirectionCards — the accept/reject card list for AI-proposed directions.
 *
 * One card per direction (component tag, headline, description, payoff) with a
 * Switch: on = build the result around it. Composed by the variant-steering
 * dialog on the recipe page (#909) and the pre-generation dialog on the create
 * page (#986) — the element is defined once here, per the library rule (§13).
 */
export function DirectionCards({ options, chosen, onToggle, toggleLabel }: DirectionCardsProps) {
  return (
    <ul className={styles.list}>
      {options.map((opt) => {
        const on = chosen.includes(opt.id);
        return (
          <li key={opt.id} className={styles.card} data-on={on}>
            <div className={styles.text}>
              <span className={styles.component}>{opt.component}</span>
              <span className={styles.title}>{opt.title}</span>
              <p className={styles.description} id={`direction-desc-${opt.id}`}>
                {opt.description} <span className={styles.payoff}>{opt.payoff}</span>
              </p>
            </div>
            {/* describedBy carries the description + payoff to whoever tabs
                switch-to-switch — the whole reason to accept or reject (#994). */}
            <Switch
              checked={on}
              onChange={(next) => onToggle(opt.id, next)}
              label={toggleLabel(opt)}
              describedBy={`direction-desc-${opt.id}`}
              tone="sage"
              className={styles.switch}
            />
          </li>
        );
      })}
    </ul>
  );
}
